import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { interval, Subscription, timer } from 'rxjs';
import { NgClass } from '@angular/common';
import { ShoppingCartState } from '../../states/shopping-cart-state';
import { AmountInterface } from '@interfaces/shopping-cart/amount-interface';

@Component({
  selector: 'ln-product-item-card-quantity-selector',
  imports: [ReactiveFormsModule, NgClass],
  templateUrl: './product-item-card-quantity-selector.component.html',
  styleUrl: './product-item-card-quantity-selector.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductItemCardQuantitySelectorComponent implements OnInit, OnChanges, OnDestroy {
  private TAG = 'ProductItemCardQuantitySelectorComponent';
  @Input({ required: true }) quantity!: number;
  @Input({ required: true }) productID!: string;
  @Input({ required: true }) showButonSpinner!: WritableSignal<boolean>;
  form!: FormGroup;
  private fb = inject(FormBuilder);
  buttonIncrementPressed = signal(false);
  buttonDecrementPressed = signal(false);
  private shoppingCartState = inject(ShoppingCartState);
  private changesTimerSubscription: Subscription | undefined = undefined;
  ngOnInit(): void {
    this.form = this.fb.group({
      quantity: [this.quantity ?? 1],
    });

    this.form.get('quantity')?.valueChanges.subscribe(value => {
      const clean = this.cleanValue(value);
      if (clean !== value) {
        this.form.get('quantity')?.setValue(clean ? clean : '', { emitEvent: false });
      }
    });
  }

  ngOnChanges() {
    if (this.form?.get('quantity') && this.quantity !== this.form.get('quantity')?.value) {
      this.form.get('quantity')?.setValue(this.quantity, { emitEvent: false });
    }
  }
  ngOnDestroy(): void {
    this.cancelChangesTimer();
  }
  private cleanValue(value: string | number): number | undefined {
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (trimmed === '' || trimmed === null || trimmed === undefined || Number(trimmed) === 0) {
      return undefined;
    }
    return Number(trimmed);
  }

  protected increment() {
    const amount: AmountInterface = { amount: this.quantity + 1 };
    this.incrementTimer();
    this.showButonSpinner.set(true);
    this.shoppingCartState.updateItem(this.productID, amount).subscribe(() => {
      console.log(`${this.TAG} ${this.productID} ${amount}`);
    });
  }

  protected decrement() {
    if (this.quantity > 1) {
      this.showButonSpinner.set(true);
      this.decrementTimer();
      const amount: AmountInterface = { amount: this.quantity - 1 };
      this.shoppingCartState.updateItem(this.productID, amount).subscribe(() => {
        console.log(`${this.TAG} ${this.productID} ${amount}`);
      });
    }
  }

  private changeQuantity() {
    const quantity: number = this.form.get('quantity')?.value ?? 0;
    if (quantity && this.quantity != quantity) {
      this.showButonSpinner.set(true);
      const amount: AmountInterface = { amount: quantity };
      this.shoppingCartState.updateItem(this.productID, amount).subscribe(() => {
        console.log(`${this.TAG} ${this.productID} ${amount}`);
      });
    } else {
      this.form.get('quantity')?.setValue(this.quantity);
    }
  }

  protected startChangesTimer(event: KeyboardEvent) {
    this.cancelChangesTimer();
    if (event.key === 'Enter') {
      this.changeQuantity();
    } else {
      this.changesTimer();
    }
  }

  private changesTimer(): void {
    this.changesTimerSubscription = timer(1000).subscribe(() => {
      this.cancelChangesTimer();
      this.changeQuantity();
    });
  }

  private cancelChangesTimer(): void {
    this.changesTimerSubscription?.unsubscribe();
    this.changesTimerSubscription = undefined;
  }

  protected onPaste(event: ClipboardEvent) {
    event.preventDefault(); // Completely blocks pasting
  }

  private incrementTimer() {
    this.buttonIncrementPressed.update(actualValue => !actualValue);
    const timerSubscription = interval(10).subscribe(() => {
      this.buttonIncrementPressed.update(actualValue => !actualValue);
      timerSubscription.unsubscribe();
    });
  }
  private decrementTimer() {
    this.buttonDecrementPressed.update(actualValue => !actualValue);
    const timerSubscription = interval(10).subscribe(() => {
      this.buttonDecrementPressed.update(actualValue => !actualValue);
      timerSubscription.unsubscribe();
    });
  }
}
